import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { Input } from '../../../components/items/joinInput';
import DatePicker from 'react-datepicker';
import { ko } from 'date-fns/locale';
import {
  Calendar,
  DonwArrowIcon,
  SettingIcon,
} from '../../../components/icon/icon';
import { useEffect, useState } from 'react';
import Select from 'react-select';
import { formatLocalDate, formatNumber, formatTime } from '../../../utils/util';
import { UserInputSetting } from '../../../components/user/dairy/user.input.setting';
import { AnimatePresence, motion } from 'framer-motion';
import { useHistory, useParams } from 'react-router-dom';
import { defaultValuesBelonged } from '../../../utils/reportDefaultValues';
import { zodResolver } from '@hookform/resolvers/zod';
import { reportBelongedRegistSchema } from '../../../schema/reportSchema';
import { fetchDriverAmount } from '../../../api/mypage';
import {
  getCorporationList,
  reportBelongedDetail,
  reportUpdateBelonged,
} from '../../../api/report';
import { AlertModal } from '../../../components/modal/alert.modal';

const workTypeSelectItems = [
  { value: 'full', label: '전일' },
  { value: 'half', label: '반일' },
];

interface RegistrationReportbelongedDto {
  corporationId: number; // 회사 아이디
  arrivedAt: string | Date | null; // 도착시
  halfDayAmount: number; // 반일 금액
  fullDayAmount: number; // 전일 금액
  goWorkTime: string | Date | null; // 출근 시간
  leaveWorkTime: string | Date | null; // 퇴근 시간
  dayType: string; // 작업 형태
  ryubeiVolume?: number; // 루베량 (optional)
  cycleVolume?: number; // 회전수 (optional)
  workAmount: number; // 작업 금액
  ausAmount?: number; // 요소수 금액 (optional)
  ausVolume?: number; // 요소수 주유량 (optional)
  refuelingAmount?: number; // 주유 가격 (optional)
  refuelingVolume?: number; // 주유량 (optional)
  operationDistance?: number; // 운행 거리 (optional)
  additionAmount?: number; // 추가 작업 금액 (optional)
  additionComment?: string; // 추가 작업 내용 (optional)
  totalAmount: number; // 총 수입 금액
  autoTotalAmount: number; // 자동 계산 금액
}

interface CorporationListDto {
  corporationId: number;
  corporationName: string;
}

const UserDiaryBelongedUpdate = () => {
  const { id } = useParams<{ id: string }>();
  const today = new Date();

  const defaultValues = defaultValuesBelonged;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<RegistrationReportbelongedDto>({
    defaultValues,
    resolver: zodResolver(reportBelongedRegistSchema),
  });

  const dayType = watch('dayType'); // 작업 형태 값 감시
  const halfDayAmount = watch('halfDayAmount'); // 반일 금액 감시
  const fullDayAmount = watch('fullDayAmount'); // 전일 금액 감시
  const workAmount = watch('workAmount');
  const additionAmount = watch('additionAmount');

  const [targetDate, setTargetDate] = useState<Date>(today);
  const [startTime, setStartTime] = useState<Date>(new Date());
  const [endTime, setEndTime] = useState<Date>(new Date());
  const [etcSettingOpen, setEtcSettingOpen] = useState(false);
  const [switchStates, setSwitchStates] = useState<boolean[]>([]); // 추가된 상태

  // 모달
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [navigateOnClose, setNavigateOnClose] = useState(false); // 페이지 이동 여부를 추적

  const [corporationList, setCorporationList] = useState<CorporationListDto[]>(
    [],
  );

  const handleModalClose = () => {
    setIsModalOpen(false);
    if (navigateOnClose) {
      history.push({
        pathname: '/userDiary',
      });
    }
  };

  const handleSettingOpen = () => {
    setEtcSettingOpen(true);
  };
  const handleSettingClose = () => {
    setEtcSettingOpen(false);
  };

  const handleSaveSettings = (settings: boolean[]) => {
    setSwitchStates(settings); // 스위치 상태값 저장
  };

  useEffect(() => {
    const getStandardAmount = async () => {
      try {
        const data = await fetchDriverAmount();
        // halfDayAmount가 0이면 설정하지 않음
        if (Number(data.halfDay) !== 0) {
          setValue('halfDayAmount', Number(data.halfDay));
        }
        if (Number(data.fullDay) !== 0) {
          setValue('fullDayAmount', Number(data.fullDay));
        }
      } catch (error) {
        console.error(error);
      }
    };

    getStandardAmount();
  }, []);

  useEffect(() => {
    const getCorporationListData = async () => {
      try {
        const response = await getCorporationList();
        setCorporationList(response);
      } catch (error) {
        console.error(error);
      }
    };

    getCorporationListData();
  }, []);

  const onSubmit = async (data: RegistrationReportbelongedDto) => {
    const formData = {
      ...data,
      // 날짜 필드를 변환
      arrivedAt: data.arrivedAt
        ? formatLocalDate(new Date(data.arrivedAt))
        : '',
      goWorkTime: data.goWorkTime ? formatTime(new Date(data.goWorkTime)) : '',
      leaveWorkTime: data.leaveWorkTime
        ? formatTime(new Date(data.leaveWorkTime))
        : '',
      // 필요한 경우 추가적인 날짜 필드를 여기서 변환
    };

    try {
      const response = await reportUpdateBelonged(Number(id), formData);
      if (response.statusCode === 200) {
        setModalMessage(response.message);
        setIsModalOpen(true);
        setNavigateOnClose(true);
      } else {
        setModalMessage('일보 - 일대차 수정에 실패했습니다.');
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (dayType === 'full') {
      // 전일 선택 시
      setValue('workAmount', Number(fullDayAmount)); // '전일' 금액 설정
    } else if (dayType === 'half') {
      // 반일 선택 시
      setValue('workAmount', Number(halfDayAmount)); // '반일' 금액 설정
    }
  }, [dayType, fullDayAmount, halfDayAmount, setValue]); // dayType, fullDayAmount, halfDayAmount 변경 시 실행

  useEffect(() => {
    const total = Number(workAmount) + Number(additionAmount);

    setValue('corporationId', total);
    setValue('autoTotalAmount', total);
  }, [workAmount, additionAmount]);

  useEffect(() => {
    const getLoadData = async () => {
      try {
        const response = await reportBelongedDetail(Number(id));
        console.log('일대차 수정 response', response);
        setValue('corporationId', response.corporationId);
        setValue(
          'arrivedAt',
          response.arrivedAt ? new Date(response.arrivedAt) : null,
        );
        setValue('halfDayAmount', response.halfDayAmount);
        setValue('fullDayAmount', response.fullDayAmount);
        // goWorkTime을 "HH:mm:ss"에서 Date 객체로 변환
        if (response.goWorkTime) {
          const [hours, minutes, seconds] = response.goWorkTime.split(':');
          const goWorkDate = new Date();
          goWorkDate.setHours(Number(hours), Number(minutes), Number(seconds));
          setValue('goWorkTime', goWorkDate);
        }
        // leaveWorkTime을 "HH:mm:ss"에서 Date 객체로 변환
        if (response.leaveWorkTime) {
          const [hours, minutes, seconds] = response.leaveWorkTime.split(':');
          const leaveWorkDate = new Date();
          leaveWorkDate.setHours(
            Number(hours),
            Number(minutes),
            Number(seconds),
          );
          setValue('leaveWorkTime', leaveWorkDate);
        }
        setValue('dayType', response.dayType);
        setValue('ryubeiVolume', response.ryubeiVolume);
        setValue('cycleVolume', response.cycleVolume);
        setValue('workAmount', response.workAmount);
        setValue('additionComment', response.additionComment);
        setValue('additionAmount', response.additionAmount);
        setValue('totalAmount', response.totalAmount);

        setValue('ausAmount', response.ausAmount);
        setValue('ausVolume', response.ausVolume);

        setValue('refuelingAmount', response.refuelingAmount);
        setValue('refuelingVolume', response.refuelingVolume);

        setValue('operationDistance', response.operationDistance);

        // settings 배열을 변경하기 위한 조건 설정
        const ausCondition =
          Number(response.ausAmount) !== 0 || Number(response.ausVolume) !== 0;
        const refuelingCondition =
          Number(response.refuelingAmount) !== 0 ||
          Number(response.refuelingVolume) !== 0;
        const operationCondition = Number(response.operationDistance) !== 0;

        // settings 값 설정 로직
        const settings = [
          ausCondition, // true이면 aus 관련 값을 활성화
          refuelingCondition, // true이면 refueling 관련 값을 활성화
          operationCondition, // true이면 operation 관련 값을 활성화
        ];

        // 상태값 저장
        handleSaveSettings(settings);
      } catch (error) {
        console.error(error);
      }
    };

    getLoadData();
  }, [id, setValue]);

  return (
    <Container>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="select-wrapper">
          <span>회사명</span>
          <Controller
            name="corporationId"
            control={control}
            render={({ field }) => (
              <Select
                className="search-select"
                classNamePrefix="react-select"
                options={corporationList.map((corp) => ({
                  value: corp.corporationId, // corporationName을 value로 설정
                  label: corp.corporationName, // corporationName을 label로 설정
                }))}
                onChange={(selectedOption) =>
                  field.onChange(selectedOption ? selectedOption.value : null)
                }
                value={corporationList
                  .map((corp) => ({
                    value: corp.corporationId,
                    label: corp.corporationName,
                  }))
                  .find((option) => option.value === field.value)} // corporationName을 비교하여 현재 선택된 값을 설정
                components={{ IndicatorSeparator: () => null }}
                placeholder="회사 선택"
                menuPlacement="top"
                maxMenuHeight={100}
              />
            )}
          />
        </div>
        {errors.corporationId && (
          <ValTxt>{String(errors.corporationId.message)}</ValTxt>
        )}
        <DatePickerContainer>
          <span>도착일</span>
          <div className="inner">
            <Controller
              name="arrivedAt"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomDatePicker
                  className="search-input"
                  locale={ko}
                  dateFormat="yyyy-MM-dd"
                  selected={value ? new Date(value) : null} // 문자열을 Date 객체로 변환
                  startDate={targetDate}
                  selectsStart
                  onChange={(date: Date | null) => onChange(date)} // Date 객체로 전달
                  placeholderText="날짜 선택"
                  withPortal
                />
              )}
            />
            <div className="icon-box">
              <Calendar />
            </div>
          </div>
        </DatePickerContainer>
        {errors.arrivedAt && (
          <ValTxt>{String(errors.arrivedAt.message)}</ValTxt>
        )}
        <span className="set-cost-title">기준 금액 설정</span>
        <div className="row-50">
          <div className="cost-wrapper">
            <Controller
              name="halfDayAmount"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Input
                  label="반일"
                  name="halfDayAmount"
                  value={value ? formatNumber(Number(value)) : ''}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/[^0-9]/g, '');
                    onChange(rawValue);
                  }}
                  placeholder="금액입력"
                />
              )}
            />
          </div>
          <div className="cost-wrapper">
            <Controller
              name="fullDayAmount"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Input
                  label="전일"
                  name="fullDayAmount"
                  value={value ? formatNumber(Number(value)) : ''}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/[^0-9]/g, '');
                    onChange(rawValue);
                  }}
                  placeholder="금액입력"
                />
              )}
            />
          </div>
        </div>

        <div className="row-50">
          <DatePickerContainer>
            <span>출근시간</span>
            <div className="inner">
              <Controller
                name="goWorkTime"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CustomDatePicker
                    className="search-input"
                    selected={value ? new Date(value) : null}
                    onChange={(date: any) => onChange(date)}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="출근시간"
                    dateFormat="hh:mm aa"
                    withPortal
                    placeholderText={startTime.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })} // 문자열로 변환
                  />
                )}
              />
              <div className="icon-box">
                <DonwArrowIcon />
              </div>
            </div>
          </DatePickerContainer>
          <DatePickerContainer>
            <span>퇴근시간</span>
            <div className="inner">
              <Controller
                name="leaveWorkTime"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CustomDatePicker
                    className="search-input"
                    selected={value ? new Date(value) : null}
                    onChange={(date: any) => onChange(date)}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="퇴근시간"
                    dateFormat="hh:mm aa"
                    withPortal
                    placeholderText={endTime.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })} // 문자열로 변환
                  />
                )}
              />
              <div className="icon-box">
                <DonwArrowIcon />
              </div>
            </div>
          </DatePickerContainer>
        </div>
        {errors.goWorkTime && (
          <ValTxt>{String(errors.goWorkTime.message)}</ValTxt>
        )}
        {errors.leaveWorkTime && (
          <ValTxt>{String(errors.leaveWorkTime.message)}</ValTxt>
        )}
        <div className="select-wrapper">
          <span>작업형태</span>
          <Controller
            name="dayType"
            control={control}
            render={({ field }) => (
              <Select
                className="search-select"
                classNamePrefix="react-select"
                options={workTypeSelectItems}
                onChange={(val) => field.onChange(val ? val.value : null)}
                value={workTypeSelectItems.find((c) => c.value === field.value)}
                components={{ IndicatorSeparator: () => null }}
                placeholder="작업 형태 선택"
                maxMenuHeight={200}
              />
            )}
          />
        </div>
        {errors.dayType && <ValTxt>{String(errors.dayType.message)}</ValTxt>}
        <div className="row-50">
          <div className="wrapper">
            <Controller
              name="ryubeiVolume"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Input
                  label="루베"
                  name="ryubeiVolume"
                  value={value ? formatNumber(Number(value)) : ''}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/[^0-9]/g, '');
                    onChange(rawValue);
                  }}
                  placeholder="m3 단위 입력"
                />
              )}
            />
          </div>
          <div className="wrapper">
            <Controller
              name="cycleVolume"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Input
                  label="회전수"
                  name="cycleVolume"
                  value={value ? formatNumber(Number(value)) : ''}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/[^0-9]/g, '');
                    onChange(rawValue);
                  }}
                  placeholder="숫자 입력"
                />
              )}
            />
          </div>
        </div>
        <div className="wrapper">
          <Controller
            name="workAmount"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                label="작업 금액"
                name="workAmount"
                value={value ? formatNumber(Number(value)) : ''}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/[^0-9]/g, '');
                  onChange(rawValue);
                }}
                placeholder="금액 입력"
              />
            )}
          />
        </div>
        <div className="etc-setting-btn-box">
          <button type="button" onClick={handleSettingOpen}>
            <span>입력 설정</span>
            <span className="icon-box">
              <SettingIcon />
            </span>
          </button>
        </div>
        {switchStates[0] ? (
          <div className="row-50">
            <div className="cost-wrapper">
              <Controller
                name="ausAmount"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    label="요소수 금액"
                    name="ausAmount"
                    value={value ? formatNumber(Number(value)) : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      onChange(rawValue);
                    }}
                    placeholder="금액입력"
                  />
                )}
              />
            </div>
            <div className="cost-wrapper">
              <Controller
                name="ausVolume"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    label="요소수 주유량"
                    name="ausVolume"
                    value={value ? formatNumber(Number(value)) : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      onChange(rawValue);
                    }}
                    placeholder="숫자 입력"
                  />
                )}
              />
            </div>
          </div>
        ) : null}
        {switchStates[1] ? (
          <div className="row-50">
            <div className="cost-wrapper">
              <Controller
                name="refuelingAmount"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    label="주유 가격 / L"
                    name="refuelingAmount"
                    value={value ? formatNumber(Number(value)) : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      onChange(rawValue);
                    }}
                    placeholder="금액입력"
                  />
                )}
              />
            </div>
            <div className="cost-wrapper">
              <Controller
                name="refuelingVolume"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    label="주유량(L)"
                    name="refuelingVolume"
                    value={value ? formatNumber(Number(value)) : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      onChange(rawValue);
                    }}
                    placeholder="주유량 입력"
                  />
                )}
              />
            </div>
          </div>
        ) : null}
        {switchStates[2] ? (
          <div className="cost-wrapper">
            <Controller
              name="operationDistance"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Input
                  label="운행거리"
                  name="operationDistance"
                  value={value ? formatNumber(Number(value)) : ''}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/[^0-9]/g, '');
                    onChange(rawValue);
                  }}
                  placeholder="km 단위"
                />
              )}
            />
          </div>
        ) : null}
        <Input
          type="text"
          label="추가 작업 내용"
          placeholder="내용 입력"
          {...register('additionComment')}
        />
        <div className="wrapper">
          <Controller
            name="additionAmount"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                label="추가 작업 금액"
                name="additionAmount"
                value={value ? formatNumber(Number(value)) : ''}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/[^0-9]/g, '');
                  onChange(rawValue);
                }}
                placeholder="추가 금액 입력"
              />
            )}
          />
        </div>
        <div className="wrapper">
          <Controller
            name="autoTotalAmount"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                label="자동 계산 금액 (수정 불가)"
                name="autoTotalAmount"
                value={value ? formatNumber(Number(value)) : ''}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/[^0-9]/g, '');
                  onChange(rawValue);
                }}
                placeholder="자동 계산"
                readOnly
              />
            )}
          />
        </div>
        <div className="wrapper">
          <Controller
            name="totalAmount"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                label="총 수익 금액 (수정 가능)"
                name="totalAmount"
                value={value ? formatNumber(Number(value)) : ''}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/[^0-9]/g, '');
                  onChange(rawValue);
                }}
                placeholder="자동 계산"
              />
            )}
          />
        </div>
        <ConfirmBtn onClick={() => {}}>등록 하기</ConfirmBtn>
      </form>
      <AnimatePresence initial={false}>
        {etcSettingOpen ? (
          <SearchConditionWrap
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              type: 'fade',
            }}
          >
            <UserInputSetting
              handleSearchClose={handleSettingClose}
              onSaveSettings={handleSaveSettings}
              initialSwitchStates={switchStates}
              selectType="belonged"
            />
          </SearchConditionWrap>
        ) : null}
      </AnimatePresence>

      {isModalOpen && (
        <AlertModal
          handleConfirmClose={handleModalClose}
          confirmText={modalMessage}
          onClick={handleModalClose}
        />
      )}
    </Container>
  );
};

export default UserDiaryBelongedUpdate;

// style
const Container = styled.div`
  padding: 1.5rem 2.2rem;
  height: calc(100% - 9rem);
  overflow: scroll;
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    .select-wrapper {
      padding: 1.1rem 1.1rem 1.6rem 1.5rem;
      background-color: ${(props) => props.theme.gray100};
      border-radius: 0.8rem;
      display: flex;
      flex-direction: column;
      font-size: 1.6rem;
      font-weight: 600;
      span {
        font-size: 1.2rem;
        font-weight: 600;
        color: ${(props) => props.theme.joinLabelColor};
        margin-bottom: 0.6rem;
        line-height: 1.5rem;
      }
      .search-select {
        .react-select__placeholder {
          color: ${(props) => props.theme.placeholderColor};
          font-weight: 400;
        }
        .react-select__control {
          background-color: transparent;
          min-height: 2rem;
          height: 2rem;
          border: none;
          .react-select__value-container {
            padding: 0;
          }
        }
        .react-select__indicator {
          padding: 0;
        }
      }
    }
    .set-cost-title {
      margin: 0.4rem 1rem;
      font-size: 1.4rem;
      font-weight: 600;
    }
    .cost-wrapper {
      div {
        > div {
          background-color: ${(props) => props.theme.costBgColor};
        }
      }
    }
    .row-50 {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 0.9rem;
      .cost-wrapper {
        div {
          > div {
            background-color: ${(props) => props.theme.costBgColor};
          }
        }
      }
    }
    .etc-setting-btn-box {
      display: flex;
      button {
        padding: 1.8rem 1rem;
        outline: none;
        border: none;
        background-color: transparent;
        display: flex;
        align-items: center;
        gap: 1.2rem;
        span {
          font-size: 1.6rem;
          font-weight: 600;
          line-height: 2rem;
        }
        .icon-box {
          display: flex;
        }
      }
    }
  }
  .radio-box-wrapper {
    padding: 0.3rem 0;
  }
`;

const ConfirmBtn = styled.button`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  min-height: 9.4rem;
  border: none;
  outline: none;
  padding: 1.7rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background-color: ${(props) => props.theme.btnStrongBlueColor};
  color: ${(props) => props.theme.textWhiteColor};
  font-size: 1.8rem;
  font-weight: 600;
`;

const CustomDatePicker = styled(DatePicker)``;

const DatePickerContainer = styled.div`
  padding: 1.1rem 1.1rem 1.6rem 1.5rem;
  background-color: ${(props) => props.theme.gray100};
  border-radius: 0.8rem;
  display: flex;
  flex-direction: column;
  span {
    font-size: 1.2rem;
    font-weight: 600;
    color: ${(props) => props.theme.joinLabelColor};
    margin-bottom: 0.6rem;
    line-height: 1.5rem;
  }
  .inner {
    position: relative;
    width: 100%;
    min-height: 2.4rem;
    display: flex;
    align-items: flex-end;
    .search-input {
      width: 100%;
      font-size: 1.6rem;
      font-weight: 400;
      background-color: inherit;
      border: 0px;
      outline: none;
      &::placeholder {
        color: ${(props) => props.theme.placeholderColor};
      }
    }
    .react-datepicker {
      font-size: 1.4rem;
      font-weight: 400;
      .react-datepicker__header {
        background-color: ${(props) => props.theme.textWhiteColor};
      }
      .react-datepicker__time-container
        .react-datepicker__time
        .react-datepicker__time-box {
        width: 20rem;
      }
      .react-datepicker__time-container {
        width: 20rem;
      }
      .react-datepicker__time-container
        .react-datepicker__time
        .react-datepicker__time-box
        ul.react-datepicker__time-list
        li.react-datepicker__time-list-item {
        width: 100%;
        height: 4.2rem;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 1.8rem;
      }
      .react-datepicker__current-month,
      .react-datepicker-time__header,
      .react-datepicker-year-header {
        font-size: 1.4rem;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .react-datepicker__day-name,
      .react-datepicker__day,
      .react-datepicker__time-name {
        width: 3rem;
        line-height: 3rem;
      }
      .react-datepicker__day--selected,
      .react-datepicker__day--in-selecting-range,
      .react-datepicker__day--in-range,
      .react-datepicker__month-text--selected,
      .react-datepicker__month-text--in-selecting-range,
      .react-datepicker__month-text--in-range,
      .react-datepicker__quarter-text--selected,
      .react-datepicker__quarter-text--in-selecting-range,
      .react-datepicker__quarter-text--in-range,
      .react-datepicker__year-text--selected,
      .react-datepicker__year-text--in-selecting-range,
      .react-datepicker__year-text--in-range {
        border-radius: 2rem;
        background-color: ${(props) => props.theme.btnBlueColor};
        color: ${(props) => props.theme.textWhiteColor};
      }
    }
    .react-datepicker-popper[data-placement='bottom']
      .react-datepicker__triangle {
      fill: ${(props) => props.theme.textWhiteColor};
      color: ${(props) => props.theme.textWhiteColor};
    }
    .icon-box {
      display: flex;
      position: absolute;
      top: 50%;
      right: 0.1rem;
      transform: translateY(-50%);
      svg {
        path {
          fill: #d7dbde;
        }
      }
    }
  }
`;

const SearchConditionWrap = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: 'flex';
  justify-content: 'center';
  align-items: 'center';
  cursor: 'pointer';
  width: 100%;
  height: 100%;
  .overlay {
    display: flex;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }
`;

const ValTxt = styled.span`
  color: ${(props) => props.theme.errorColor};
  font-size: 1.2rem;
  font-weight: 400;
  display: flex;
  margin: 0 1.7rem 0;
`;
